import { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as d3 from 'd3';

import { AnnotationContext } from "../context/AnnotationContext";

import Annotation from "../models/Annotation";

import "../stylesheets/image-labeler.scss";

/**
 * Anchor points for adjusting the size of a bounding box at the corners and edges.
 *
 * @method x {(Annotation) => Number} - Function to get the x-coordinate of the annotation's anchor
 * @method y {(Annotation) => Number} - Function to get the y-coordinate of the annotation's anchor
 * @method update {(Annotation, x: Number, y: Number) => void} - Function to update the annotation when the anchor
 *                                                               position is updated
 * @field cursor {String} - The type of cursor to show when the mouse is hovering over the anchor
 */
const ANCHOR_POINTS = [
  {
    x: (annotation) => annotation.x0,
    y: (annotation) => annotation.y0,
    update: (annotation, x, y) => annotation.setTopLeft(x, y),
    cursor: 'nwse-resize',
  },
  {
    x: (annotation) => (annotation.x0 + annotation.x1) / 2,
    y: (annotation) => annotation.y0,
    update: (annotation, x, y) => annotation.setTop(y),
    cursor: 'ns-resize',
  },
  {
    x: (annotation) => annotation.x1,
    y: (annotation) => annotation.y0,
    update: (annotation, x, y) => annotation.setTopRight(x, y),
    cursor: 'nesw-resize',
  },
  {
    x: (annotation) => annotation.x1,
    y: (annotation) => (annotation.y0 + annotation.y1) / 2,
    update: (annotation, x, y) => annotation.setRight(x),
    cursor: 'ew-resize',
  },
  {
    x: (annotation) => annotation.x1,
    y: (annotation) => annotation.y1,
    update: (annotation, x, y) => annotation.setBottomRight(x, y),
    cursor: 'nwse-resize',
  },
  {
    x: (annotation) => (annotation.x0 + annotation.x1) / 2,
    y: (annotation) => annotation.y1,
    update: (annotation, x, y) => annotation.setBottom(y),
    cursor: 'ns-resize',
  },
  {
    x: (annotation) => annotation.x0,
    y: (annotation) => annotation.y1,
    update: (annotation, x, y) => annotation.setBottomLeft(x, y),
    cursor: 'nesw-resize',
  },
  {
    x: (annotation) => annotation.x0,
    y: (annotation) => (annotation.y0 + annotation.y1) / 2,
    update: (annotation, x, y) => annotation.setLeft(x),
    cursor: 'ew-resize',
  }
]

/**
 * Component for creating new Labels on an image. Press 'n' to create a new label and 'esc' to cancel creating
 * a new label
 */
function ImageLabeler() {
  const annotationContext = useContext(AnnotationContext)

  const {
    annotations,
    currentAnnotation,
    setAnnotations,
    selectedAnnotation,
    setSelectedAnnotation,
    imageSrc,
    selectedImageIndex,
  } = annotationContext


  const [isDrawing, setIsDrawing] = useState(false)

  // Reference to the svg canvas
  const canvasRef = useRef(null)

  // Reference to the new Annotation when it is being created
  const newAnnotationRef = useRef(null)

  /**
   * Callback to update the position of the rectangle and anchor points of the given annotation
   *
   * @param {Annotation} annotation - The annotation to update the rectangle and anchor points for
   */
  const updateSelectedRect = useCallback((annotation) => {
    // Update the positions of the anchor points on the selected rectangle
    canvasRef.current.selectAll('circle')
        .data(ANCHOR_POINTS)
        .join('circle')
        .attr('cx', (anchor) => anchor.x(annotation))
        .attr('cy', (anchor) => anchor.y(annotation))

    // Update the position and dimensions of the selected rectangle
    canvasRef.current.selectAll('rect.selected')
        .attr('x', (annotation) => annotation.getX())
        .attr('y', (annotation) => annotation.getY())
        .attr('width', (annotation) => annotation.getWidth())
        .attr('height', (annotation) => annotation.getHeight())
  }, [])

  /**
   * Callback to add anchors to the given annotation
   *
   * @param {Annotation} annotation - The annotation to create anchors for
   */
  const addAnchors = useCallback((annotation) => {
    // Handler for updating the annotation when an anchor point is being dragged
    const dragAnchor = d3.drag().on('drag', function(dragEvent, anchor) {
      anchor.update(annotation, dragEvent.x, dragEvent.y)
      updateSelectedRect(annotation)
    })

    // Create anchor points for the selected rectangle
    canvasRef.current.selectAll('circle')
        .data(ANCHOR_POINTS)
        .join('circle')
        .attr('cx', (anchor) => anchor.x(annotation))
        .attr('cy', (anchor) => anchor.y(annotation))
        .attr('r', 3)
        .attr('fill', 'blue')
        .style('cursor', (anchor) => anchor.cursor)
        .on('mouseover', function() { d3.select(this).attr('r', 4) })
        .on('mouseout', function() { d3.select(this).attr('r', 3) })
        .call(dragAnchor)
  }, [updateSelectedRect])

  /**
   * Setup the svg canvas and add keyboard event listeners
   */
  useEffect(() => {
    const handleKeyUp = (event) => {
      if (event.key === 'n') {
        newAnnotationRef.current = null
        setIsDrawing(true)
      } else if (event.key === 'Escape') {
        setIsDrawing(false)
        // remove the rectangle that was being drawn
        canvasRef.current.selectAll('rect.drawing').remove()
        setSelectedAnnotation(null)
      }
    }

    canvasRef.current = d3.select('.d3-container').append('svg')
    document.addEventListener('keyup', handleKeyUp)

    return () => {
      d3.select('.d3-container svg').remove()
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [setSelectedAnnotation])

  /**
   * Render all annotations on the context
   */
  useEffect(() => {
    // Handler to update the annotation position when it is being dragged

    //Extension #3, Confine Labels to the Image
    const imageWidth = document.querySelector('img').offsetWidth
    const imageHeight = document.querySelector('img').offsetHeight

    const dragBox = d3.drag().on('drag', function(dragEvent, annotation) {
      if (annotation === selectedAnnotation) {
        const newX = annotation.getX() + dragEvent.dx
        const newY = annotation.getY() + dragEvent.dy

        // Check if the new position is within the boundaries of the image
        if (newX >= 0 && newX + annotation.getWidth() <= imageWidth
            && newY >= 0 && newY + annotation.getHeight() <= imageHeight) {
          annotation.updatePos(dragEvent.dx, dragEvent.dy)
          updateSelectedRect(annotation)
        }
      }
    });


    // Add rectangle for each annotation
    canvasRef.current.selectAll('rect')
        .data(currentAnnotation ? currentAnnotation : [])
        .join('rect')
        .attr('x', (annotation) => annotation.getX())
        .attr('y', (annotation) => annotation.getY())
        .attr('width', (annotation) => annotation.getWidth())
        .attr('height', (annotation) => annotation.getHeight())
        .attr('fill', 'none')
        .attr('stroke', (annotation) => annotation === selectedAnnotation ? 'blue' : 'black')
        .classed('selected', (annotation) => annotation === selectedAnnotation)
        .classed('drawing', false)
        .on('mouseover', function() { d3.select(this).style('stroke-width', 3) })
        .on('mouseout', function() { d3.select(this).style('stroke-width', 1) })
        .on('click', (_, annotation) => setSelectedAnnotation(annotation))
        .call(dragBox)

    // if (selectedAnnotation) {
    //   addAnchors(selectedAnnotation)
    // } else {
    //   // Remove all anchor points if no annotation is selected
    //   canvasRef.current.selectAll('circle').remove()
    // }
    // Tried extension #1
    if (selectedAnnotation && selectedAnnotation.testId === imageSrc.slice(-8)) {
        console.log("copied annotation", selectedAnnotation)
      addAnchors(selectedAnnotation)
    } else {
      // Remove all anchor points if no annotation is selected
      canvasRef.current.selectAll('circle').remove()
    }
  }, [annotations, selectedAnnotation, setSelectedAnnotation, addAnchors, updateSelectedRect, selectedImageIndex])

  // set the selectedAnnotation state
  useEffect(() => {
    if (annotations[selectedImageIndex]) {
      setSelectedAnnotation(annotations[selectedImageIndex])
    }
  }, [annotations, selectedImageIndex])

  /**
   * Add a new rectangle for an annotation that is being created
   *
   * @param {Annotation} annotation - The annotation to add a rectangle for
   */
  const addRectangle = (annotation) => {
    // unselect previously selected rectangle
    canvasRef.current.selectAll('rect.selected')
        .attr('stroke', 'black')
        .classed('selected', false)

    // remove any anchor points
    canvasRef.current.selectAll('circle').remove()

    // add the new rectangle
    canvasRef.current.append('rect')
        .attr('x', annotation.getX())
        .attr('y', annotation.getY())
        .attr('width', annotation.getWidth())
        .attr('height', annotation.getHeight())
        .attr('fill', 'none')
        .attr('stroke', 'blue')
        .classed('drawing', true)
  }

  /**
   * Handle a click event on the image. Is either the start or end of creating a new annotation rectangle.
   * Must be in drawing mode else the click is ignored.
   *
   * @param {NativeEvent} nativeEvent - The click event
   */
  const handleClick = ({ nativeEvent }) => {
    if (!isDrawing) { return }

    nativeEvent.preventDefault()
    nativeEvent.stopPropagation()

    if (newAnnotationRef.current) {
      stopDrawingRect()
    } else {
      startDrawingRect({ nativeEvent })
    }
  }

  /**
   * Create a new Annotation and add a rectangle to the svg.
   *
   * @param {NativeEvent} nativeEvent - The click event
   */
  const startDrawingRect = ({ nativeEvent }) => {
    // newAnnotationRef.current = new Annotation(nativeEvent.offsetX, nativeEvent.offsetY, nativeEvent.offsetX, nativeEvent.offsetY)
    // Tried extension #1
    newAnnotationRef.current = new Annotation(nativeEvent.offsetX, nativeEvent.offsetY, nativeEvent.offsetX, nativeEvent.offsetY, selectedImageIndex)

    addRectangle(newAnnotationRef.current)
  }

  /**
   * Handle a mouse move event on the image to adjust the size of a newly created annotation.
   * Must be in drawing mode and have already created a new annotation (the first click event in drawing mode)
   * otherwise the mouse move event is ignored.
   *
   * @param {NativeEvent} nativeEvent - The mouse move event
   */
  const drawRect = ({ nativeEvent }) => {
    if (!isDrawing || !newAnnotationRef.current) { return }

    nativeEvent.preventDefault()
    nativeEvent.stopPropagation()

    const annotation = newAnnotationRef.current

    annotation.x1 = nativeEvent.offsetX
    annotation.y1 = nativeEvent.offsetY

    canvasRef.current.selectAll('rect.drawing')
        .attr('x', annotation.getX())
        .attr('y', annotation.getY())
        .attr('width', annotation.getWidth())
        .attr('height', annotation.getHeight())
  }

  /**
   * Handle the second click in drawing mode to complete creating a new annotation.
   */
  const stopDrawingRect = () => {
    setIsDrawing(false)
    newAnnotationRef.current.sortBoundingBoxValues()
    setSelectedAnnotation(newAnnotationRef.current)
    setAnnotations([...annotations, newAnnotationRef.current])
  }

  return (
      <div className="image-labeler">
        <div className="image-container">
          <img src={imageSrc} alt="" />
          <div className={isDrawing ? "d3-container is-drawing" : "d3-container"}
               onClick={handleClick}
               onMouseMove={drawRect} />
        </div>
        <p>
          Press the 'n' key to enter drawing mode to create a new annotation. Once in drawing mode click on the image
          to select the first corner of the bounding box. Then make a second click to select the opposite corner of the
          bounding box. You can navigate images using arrow keys on keyboard.
        </p>
        <p>
          Press the 'esc' key to cancel creating a label and exit drawing mode.
        </p>
      </div>
  )
}

export default ImageLabeler;
